import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { GlobalCalWithoutNs } from '@calcom/embed-core';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useSelector } from 'react-redux';

import { Breadcrumb, Loader, ReturnLinkButton, Stepper } from 'components';
import { PROFILE_ROUTES, ROUTES } from 'configs';
import {
  createEnterpriseRequest,
  buildStripeCheckoutPresentmentBody,
} from 'services/subscriptions';
import { getLocation } from 'services/geolocation';
import { getAuthUser } from 'store/selectors/auth';
import { getCalApiForNamespace } from 'utils/calEmbed';
import { toast as addAppToast } from 'utils/toastAbstraction';

import { Content, Header, HeaderContainer } from 'pages/Subscriptions/styled';
import * as Ch from 'pages/Subscriptions/SubscriptionInner/styled';
import * as S from './styled';
import { OutsideAppPageContentWrapper } from 'components/Shared/styled';

/** Stable namespace for Cal embed on this page (popup modal). */
const CAL_ENTERPRISE_EMBED_NAMESPACE = 'gumbEnterpriseCheckout';

const EnterpriseCheckoutPage = () => {
  const { t } = useTranslation();
  const profile = useSelector(getAuthUser);
  const [organizationName, setOrganizationName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [enterpriseScheduleClicked, setEnterpriseScheduleClicked] =
    useState(false);

  const enterpriseCalLink = useMemo(() => {
    const direct = process.env.REACT_APP_CAL_ENTERPRISE_CAL_LINK?.trim() ?? '';
    if (direct) return direct;
    return process.env.REACT_APP_CAL_ONBOARDING_CAL_LINK?.trim() ?? '';
  }, []);

  const showEnterpriseCalCta = done && Boolean(enterpriseCalLink);

  useEffect(() => {
    setEnterpriseScheduleClicked(false);
  }, [done]);

  const calApiPromiseRef = useRef<Promise<GlobalCalWithoutNs> | null>(null);

  const openEnterpriseCalModal = useCallback(async () => {
    if (!enterpriseCalLink) return;
    if (!calApiPromiseRef.current) {
      calApiPromiseRef.current = getCalApiForNamespace({
        namespace: CAL_ENTERPRISE_EMBED_NAMESPACE,
      });
    }
    const cal = await calApiPromiseRef.current;
    cal('modal', {
      calLink: enterpriseCalLink,
      config: {
        duration: '30',
      },
    });
  }, [enterpriseCalLink]);

  const handleEnterpriseScheduleClick = useCallback(() => {
    setEnterpriseScheduleClicked(true);
    void openEnterpriseCalModal();
  }, [openEnterpriseCalModal]);

  const subscriptionPath = `${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`;
  const changePlanPath = `${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`;

  const canSubmit = useMemo(
    () => Boolean(profile?.userId) && Boolean(profile?.email),
    [profile?.userId, profile?.email],
  );

  const handleSubmit = async () => {
    if (!canSubmit || !profile?.userId || !profile?.email) {
      setSubmitError(t('enterprise.request.profileMissing'));
      return;
    }

    setSubmitError(null);
    setSubmitting(true);
    try {
      let geo: { country_code2?: string } | null = null;
      try {
        geo = await getLocation();
      } catch {
        geo = { country_code2: 'CH' };
      }
      await createEnterpriseRequest({
        user_id: profile.userId,
        email: profile.email,
        organization_name: organizationName.trim() || undefined,
        plan: 'enterprise',
        ...buildStripeCheckoutPresentmentBody(geo),
      });
      setDone(true);
      addAppToast(
        {
          title: t('enterprise.confirm.title'),
          message: t('enterprise.confirm.subtitle'),
        },
        {
          appearance: 'info',
          autoDismiss: true,
        },
      );
    } catch (err) {
      let msg = t('enterprise.request.genericError');
      if (axios.isAxiosError(err)) {
        const fromApi = (err.response?.data as { message?: string })?.message;
        if (typeof fromApi === 'string' && fromApi.length > 0) {
          msg = fromApi;
        }
      }
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OutsideAppPageContentWrapper minHeight="100svh">
      <Ch.CheckoutBackdrop>
        <Ch.EnterpriseCheckoutPageWrap>
          <Breadcrumb
            items={[
              {
                label: t('changePlan.breadcrumbSubscription'),
                to: subscriptionPath,
              },
              { label: t('enterprise.checkout.breadcrumb') },
            ]}
          />

          <Stepper
            steps={[
              {
                label: t('subscriptions.checkout.stepPlanSelected'),
                status: 'done',
                /** After the request is submitted the flow is terminal; stepper goes read-only. */
                to: done ? undefined : changePlanPath,
              },
              {
                label: t('subscriptions.checkout.stepOrderSummary'),
                status: done ? 'done' : 'active',
              },
              {
                label: t('subscriptions.checkout.stepConfirmation'),
                status: done ? 'active' : 'pending',
              },
            ]}
          />

          {done ? (
            <S.ConfirmWrap>
              <S.ConfirmCircle aria-hidden>🏢</S.ConfirmCircle>
              <S.ConfirmTitle>{t('enterprise.confirm.title')}</S.ConfirmTitle>
              <S.ConfirmSub>{t('enterprise.confirm.subtitle')}</S.ConfirmSub>
              <S.ConfirmCard>
                <S.ConfirmCardTitle>
                  {t('enterprise.confirm.nextTitle')}
                </S.ConfirmCardTitle>
                <S.ConfirmList>
                  <S.ConfirmLi>
                    <span aria-hidden>✓</span>
                    <span>{t('enterprise.confirm.step1')}</span>
                  </S.ConfirmLi>
                  <S.ConfirmLi>
                    <span aria-hidden>✓</span>
                    <span>{t('enterprise.confirm.step2')}</span>
                  </S.ConfirmLi>
                  <S.ConfirmLi>
                    <span aria-hidden>✓</span>
                    <span>{t('enterprise.confirm.step3')}</span>
                  </S.ConfirmLi>
                  <S.ConfirmLi>
                    <span aria-hidden>✓</span>
                    <span>{t('enterprise.confirm.step4')}</span>
                  </S.ConfirmLi>
                </S.ConfirmList>
              </S.ConfirmCard>
              {showEnterpriseCalCta ? (
                <S.CalScheduleWrap>
                  <S.BtnEnterpriseCalSchedule
                    type="button"
                    $pulsing={!enterpriseScheduleClicked}
                    onClick={handleEnterpriseScheduleClick}
                  >
                    {t('enterprise.confirm.bookSession')}
                  </S.BtnEnterpriseCalSchedule>
                </S.CalScheduleWrap>
              ) : null}
              {!showEnterpriseCalCta ? (
                <S.ConfirmActions>
                  <S.GoDashboardBtn to={ROUTES.HOME_PAGE}>
                    {t('enterprise.confirm.goToDashboard')}
                  </S.GoDashboardBtn>
                </S.ConfirmActions>
              ) : enterpriseScheduleClicked ? (
                <S.ConfirmActionsBtns>
                  <S.GoDashboardBtn to={ROUTES.HOME_PAGE}>
                    {t('enterprise.confirm.goToDashboard')}
                  </S.GoDashboardBtn>
                  <S.ConfirmSecondaryLink to={subscriptionPath}>
                    {t('subscriptions.checkout.stripeReturnViewSubscription')}
                  </S.ConfirmSecondaryLink>
                </S.ConfirmActionsBtns>
              ) : null}
            </S.ConfirmWrap>
          ) : (
            <S.EnterpriseCheckoutGrid>
              <S.EnterpriseOrderColumn>
                <S.EnterpriseOrderCard>
                  <S.EnterpriseOrderCardTitle>
                    {t('subscriptions.orderSummary')}
                  </S.EnterpriseOrderCardTitle>
                  <Ch.PlanSummaryRow>
                    <Ch.PlanIcon aria-hidden>🏢</Ch.PlanIcon>
                    <div>
                      <Ch.PlanInfoName>
                        {t('enterprise.checkout.planName')}
                      </Ch.PlanInfoName>
                      <Ch.PlanInfoSub>
                        {t('enterprise.checkout.planSub')}
                      </Ch.PlanInfoSub>
                      <Ch.PlanBadge $variant="enterprise">
                        {t('enterprise.checkout.badge')}
                      </Ch.PlanBadge>
                    </div>
                  </Ch.PlanSummaryRow>

                  <Ch.SpecPills>
                    <Ch.SpecPill>
                      <span aria-hidden>🏠</span>
                      <span>
                        <Ch.SpecPillVal>
                          {t('changePlan.unlimited')}
                        </Ch.SpecPillVal>{' '}
                        {t('changePlan.specCommunities')}
                      </span>
                    </Ch.SpecPill>
                    <Ch.SpecPill>
                      <span aria-hidden>👥</span>
                      <span>
                        <Ch.SpecPillVal>
                          {t('changePlan.unlimited')}
                        </Ch.SpecPillVal>{' '}
                        {t('changePlan.specMembers')}
                      </span>
                    </Ch.SpecPill>
                    <Ch.SpecPill>
                      <span aria-hidden>📋</span>
                      <span>
                        <Ch.SpecPillVal>
                          {t('changePlan.unlimited')}
                        </Ch.SpecPillVal>{' '}
                        {t('changePlan.specSchedulersMany')}
                      </span>
                    </Ch.SpecPill>
                    <Ch.SpecPill>
                      <span aria-hidden>💾</span>
                      <span>
                        <Ch.SpecPillVal>
                          {t('changePlan.custom')}
                        </Ch.SpecPillVal>{' '}
                        {t('changePlan.specStorage')}
                      </span>
                    </Ch.SpecPill>
                  </Ch.SpecPills>

                  <Ch.PriceBreakdown>
                    <Ch.PbRow>
                      <Ch.PbLabel>
                        {t('enterprise.checkout.priceLine')}
                      </Ch.PbLabel>
                      <Ch.PbVal>
                        {t('enterprise.checkout.customPricing')}
                      </Ch.PbVal>
                    </Ch.PbRow>
                    <Ch.PbRow>
                      <Ch.PbLabel>
                        {t('enterprise.checkout.billingLine')}
                      </Ch.PbLabel>
                      <Ch.PbVal>
                        {t('enterprise.checkout.invoiceAnnual')}
                      </Ch.PbVal>
                    </Ch.PbRow>
                    <Ch.PbDivider />
                    <S.EnterpriseOrderPbRowTotal>
                      <Ch.PbLabel>
                        {t('subscriptions.checkout.totalDueToday')}
                      </Ch.PbLabel>
                      <Ch.PbVal>
                        {t('subscriptions.checkout.toBeInvoiced')}
                      </Ch.PbVal>
                    </S.EnterpriseOrderPbRowTotal>
                  </Ch.PriceBreakdown>

                  <Ch.IncludedSection>
                    <Ch.IncludedTitle>
                      {t('enterprise.checkout.includedTitle')}
                    </Ch.IncludedTitle>
                    <Ch.IncludedList>
                      {Array.from({ length: 8 }, (_, i) => (
                        <Ch.IncludedItem key={`ent-inc-${i}`}>
                          <Ch.IncludedCheck aria-hidden>✓</Ch.IncludedCheck>
                          {t(`enterprise.checkout.included${i + 1}`)}
                        </Ch.IncludedItem>
                      ))}
                    </Ch.IncludedList>
                  </Ch.IncludedSection>

                  <S.EnterpriseInfoBox>
                    <strong>{t('enterprise.checkout.howItWorksTitle')}</strong>
                    {t('enterprise.checkout.howItWorksBody')}
                  </S.EnterpriseInfoBox>

                  <Ch.Guarantee>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>📋</Ch.GuaranteeIcon>
                      {t('enterprise.checkout.orderTrust1')}
                    </Ch.GuaranteeItem>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>🤝🏻</Ch.GuaranteeIcon>
                      {t('enterprise.checkout.orderTrust2')}
                    </Ch.GuaranteeItem>
                    <Ch.GuaranteeItem>
                      <Ch.GuaranteeIcon aria-hidden>📅</Ch.GuaranteeIcon>
                      {t('enterprise.checkout.orderTrust3')}
                    </Ch.GuaranteeItem>
                  </Ch.Guarantee>
                </S.EnterpriseOrderCard>
              </S.EnterpriseOrderColumn>

              <Ch.PaymentCard>
                <Ch.PaymentCardTitle>
                  {t('enterprise.checkout.requestTitle')}
                </Ch.PaymentCardTitle>
                <Ch.InvoicePriceDisplay>
                  <S.RowStack>
                    <Ch.InvoicePriceTitle>
                      {t('enterprise.checkout.customPricing')}
                    </Ch.InvoicePriceTitle>
                    <Ch.InvoicePriceSub>
                      {t('enterprise.checkout.requestSubline')}
                    </Ch.InvoicePriceSub>
                  </S.RowStack>
                </Ch.InvoicePriceDisplay>

                <S.OrgFieldWrap>
                  <S.OrgLabel htmlFor="enterprise-org">
                    {t('enterprise.request.organizationLabel')}
                  </S.OrgLabel>
                  <S.OrgInput
                    id="enterprise-org"
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    placeholder={t(
                      'enterprise.request.organizationPlaceholder',
                    )}
                  />
                </S.OrgFieldWrap>

                {submitError ? (
                  <Ch.InvoiceFormError role="alert">
                    {submitError}
                  </Ch.InvoiceFormError>
                ) : null}

                <Ch.BtnPurchase
                  type="button"
                  onClick={() => {
                    void handleSubmit();
                  }}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader type="button" size="28px" thickness={2} />
                  ) : (
                    <>
                      <span aria-hidden>📋</span>
                      {t('enterprise.request.submit')}
                    </>
                  )}
                </Ch.BtnPurchase>

                <S.SubtleNote>
                  {t('enterprise.request.noPaymentNote')}
                </S.SubtleNote>

                <Ch.Guarantee>
                  <Ch.GuaranteeItem>
                    <Ch.GuaranteeIcon aria-hidden>🤝🏻</Ch.GuaranteeIcon>
                    {t('enterprise.checkout.trust1')}
                  </Ch.GuaranteeItem>
                  <Ch.GuaranteeItem>
                    <Ch.GuaranteeIcon aria-hidden>📋</Ch.GuaranteeIcon>
                    {t('enterprise.checkout.trust2')}
                  </Ch.GuaranteeItem>
                  <Ch.GuaranteeItem>
                    <Ch.GuaranteeIcon aria-hidden>📅</Ch.GuaranteeIcon>
                    {t('enterprise.checkout.trust3')}
                  </Ch.GuaranteeItem>
                </Ch.Guarantee>
              </Ch.PaymentCard>
            </S.EnterpriseCheckoutGrid>
          )}

          {!done ? (
            <Ch.StyledCancelCheckout as={Link} to={changePlanPath}>
              {t('subscriptions.checkout.backToChangePlan')}
            </Ch.StyledCancelCheckout>
          ) : null}
        </Ch.EnterpriseCheckoutPageWrap>
      </Ch.CheckoutBackdrop>
    </OutsideAppPageContentWrapper>
  );
};

export default EnterpriseCheckoutPage;
